import { useCallback, useEffect, useMemo, useState } from "react";
import { get, post } from "@/lib/api";
import type { VenueQueueItem } from "@/types/venue";

interface PendingResponse {
  items?: VenueQueueItem[];
}

const VENUE_ENDPOINT = "/venue-queue";

export function useVenueQueue(defaultLimit = 100) {
  const [items, setItems] = useState<VenueQueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (overrideLimit?: number) => {
    setLoading(true);
    setError(null);
    try {
      const limit = typeof overrideLimit === "number" ? overrideLimit : defaultLimit;
      const endpoint =
        limit > 0 ? `${VENUE_ENDPOINT}/pending?limit=${encodeURIComponent(limit)}` : `${VENUE_ENDPOINT}/pending`;
      const res = await get<VenueQueueItem[] | PendingResponse>(endpoint);
      const next = Array.isArray(res) ? res : res?.items || [];
      setItems(next);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [defaultLimit]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = useCallback(async (id: string) => {
    await post(`${VENUE_ENDPOINT}/${id}/approve`);
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const reject = useCallback(async (id: string, reason?: string) => {
    await post(`${VENUE_ENDPOINT}/${id}/reject`, reason ? { reason } : undefined);
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return useMemo(
    () => ({ items, loading, error, load, approve, reject }),
    [items, loading, error, load, approve, reject],
  );
}
